"use client";

import * as Sentry from "@sentry/nextjs";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";

import { GhostErrorLayout } from "@/components/error/GhostErrorLayout";
import { NoTranslate } from "@/components/no-translate";
import { Route } from "@/enums/route";
import { toast } from "@/lib/toast";
import { readRecentIncidentReference } from "@/redux/services/sentryErrorCapture";

// Throttle Sentry.captureException + toast.error for retry loops where the
// same error.tsx remounts repeatedly with the same error (Sentry
// CUSTOMER-UI-3K-class render loops).
//
// Exported for unit tests only — do not consume from app code.
export const REPORT_TTL_MS = 30_000;
export const recentReports = new Map<string, number>();

/**
 * Build a compound dedup key. We must NOT be narrower than Sentry's
 * fingerprint (which uses stack + type, not just message), otherwise two
 * distinct bugs sharing a message string (e.g. `"Network Error"`) silently
 * collapse into one slot and the second is dropped.
 */
function dedupKey(error: Error & { digest?: string }): string {
  return `${error.name}:${error.message}:${error.digest ?? ""}`;
}

/**
 * Returns true if we should report this error now. Throttles repeats of the
 * same compound key within REPORT_TTL_MS. Errors without any identifying
 * information (no message AND no digest) are always reported — the intent
 * is to throttle a recurring same error, not to suppress unidentifiable
 * errors.
 */
export function shouldReport(error: Error & { digest?: string }): boolean {
  const now = Date.now();

  // GC stale entries (run before lookup so 'after TTL elapses' is a fresh slot)
  for (const [k, t] of recentReports) {
    if (now - t > REPORT_TTL_MS) recentReports.delete(k);
  }

  // No identifying info → always report; don't dedup unidentifiable errors.
  if (!error.message && !error.digest) return true;

  const key = dedupKey(error);
  const last = recentReports.get(key);
  if (last !== undefined && now - last < REPORT_TTL_MS) return false;
  recentReports.set(key, now);
  return true;
}

/**
 * Route-level error page for protected routes. The parent layout (sidebar,
 * header) stays visible, so we use GhostErrorLayout with hideSidebar=true
 * to only show ghost dashboard content in the content area.
 */
export default function ProtectedRouteError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const t = useTranslations("errorBoundary");
  const [reference] = useState(() => readRecentIncidentReference());
  const reportedRef = useRef(false);

  useEffect(() => {
    // console.error always fires (helpful in dev + Sentry breadcrumb).
    console.error(`Protected route error: ${error.message}`);

    // Gate Sentry + user toast to the first mount per unique error in TTL,
    // preventing report-spam when users click "Try again" on a persistent
    // loop. Only set the ref on actual report — otherwise a TTL-suppressed
    // mount would block a legitimate retry within this same mount lifetime.
    if (reportedRef.current) return;
    if (shouldReport(error)) {
      reportedRef.current = true;
      Sentry.captureException(error);
      toast.error(t("toast"));
    }
  }, [error, t]);

  return (
    <GhostErrorLayout hideSidebar>
      <h1 style={{ fontSize: "1.25rem", fontWeight: 600, margin: 0 }}>
        {t("pageTitle")}
      </h1>
      <p
        style={{
          fontSize: "0.875rem",
          color: "#6b7280",
          margin: 0,
          lineHeight: 1.6,
        }}
      >
        {t("pageDescription")}
      </p>
      {reference && (
        <p
          style={{
            fontSize: "0.75rem",
            color: "#9ca3af",
            margin: 0,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          }}
        >
          <span>{t("incidentReference")} </span>
          <NoTranslate>{reference}</NoTranslate>
        </p>
      )}
      <div
        style={{
          display: "flex",
          gap: "0.75rem",
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        <button
          onClick={reset}
          style={{
            padding: "0.5rem 1.25rem",
            fontSize: "0.875rem",
            fontWeight: 500,
            color: "#fff",
            backgroundColor: "#18181b",
            border: "none",
            borderRadius: "0.375rem",
            cursor: "pointer",
          }}
        >
          {t("retry")}
        </button>
        <button
          onClick={() => {
            // Hard navigation (NOT router.push): an error.tsx boundary stays in
            // its error state until reset() runs, AND after a render crash the
            // client tree / Redux state may be partially corrupted. A full
            // document navigation guarantees the user reaches the dashboard with
            // a clean slate — the most reliable recovery from an error boundary.
            window.location.assign(Route.Dashboard);
          }}
          style={{
            padding: "0.5rem 1.25rem",
            fontSize: "0.875rem",
            fontWeight: 500,
            color: "#374151",
            backgroundColor: "#fff",
            border: "1px solid #d1d5db",
            borderRadius: "0.375rem",
            cursor: "pointer",
          }}
        >
          {t("goToDashboard")}
        </button>
      </div>
    </GhostErrorLayout>
  );
}
